//
// cyclr.js - javascript cycler with selectable controls
//
//
// Attributes
// -----------------------------------------------
//
//   data-cyclr
//
//   data-cyclr--auto-slide      - optional, auto-slide if this attriubte is present
//   data-cyclr--slide-duration  - optional, if autosliding, duration of slides in seconds
//   data-cyclr--first-initially-shown  - optional, add if first slide is initially shown and auto-slide is on.
//                                        otherwise, first slide will have 2x duration - tricky to work around.
//   data-cyclr--stop-on-click          - optional, add to stop auto-sliding when an item is clicked
//   data-cyclr--items-selector  - optional, if you don't want the displayed items to be within the same wrapper, use
//                                 this option to pass a custom selector used to find the items wrapper.
//
//   data-cyclr--control-active-classes    - optional, used as defaults for all control items
//   data-cyclr--control-inactive-classes  - ~
//
//   data-cyclr--item-active-classes       - optional, used as defaults for all display items
//   data-cyclr--item-inactive-classes     - ~
//
//
// Inside the main element you'll probably have an array of individual clickable elements. Cyclr maps data-cyclr-controls
// to data-cyclr-items by index.
//
//   data-cyclr-control
//
//   data-cyclr--control-event             - optional, default is click
//   data-cyclr--control-active-classes    - optional, override the (in)active-classes for this particular item
//   data-cyclr--control-inactive-classes  - ~
//
//
// And you'll have an array of the displayed items (*may live outside the data-cyclr wrapper/kick-off element, see
// the data-cyclr--items-selector option*):
//
//   data-cyclr-item
//
//   data-cyclr--item-active-classes    - optional, override the (in)active-classes for this particular item
//   data-cyclr--item-inactive-classes  - ~
//

const default_slide_duration = 10000; // miliseconds
const ACTIVE = true;
const INACTIVE = false;

// helpers
// -----------------------------------------------

function class_array_from_attr(el, attr) {
  return (el.getAttribute(attr) || "").split(/[ ,]+/).filter(Boolean);
}

function array_or(a1, a2) {
  return a1.length ? a1 : a2;
}

function apply_classes(el, get_classes, is_active) {
  const active = get_classes(el, ACTIVE);
  const inactive = get_classes(el, INACTIVE);
  const to_add = is_active ? active : inactive;

  active.forEach((c) => el.classList.remove(c));
  inactive.forEach((c) => el.classList.remove(c));
  to_add.forEach((c) => el.classList.add(c));
}

// cyclr
// -----------------------------------------------

function cyclr(wrapper) {
  if (!wrapper) {
    return null;
  }

  function get_classes(control_or_item) {
    return (el, is_active) =>
      array_or(
        class_array_from_attr(
          el,
          `data-cyclr--${control_or_item}-${
            is_active ? "" : "in"
          }active-classes`
        ),
        class_array_from_attr(
          wrapper,
          `data-cyclr--${control_or_item}-${
            is_active ? "" : "in"
          }active-classes`
        )
      );
  }

  function set_item(i) {
    state.index = i;

    clickables.forEach((clickable, j) =>
      apply_classes(clickable, get_classes("control"), i === j)
    );
    items.forEach((item, j) =>
      apply_classes(item, get_classes("item"), i === j)
    );
  }

  // events
  // -----------------------------------------------

  function click(ev) {
    const el = ev.currentTarget;
    const index = Array.prototype.indexOf.call(clickables, el);

    if (opts.stop_on_click) {
      stop();
    }
    if (index !== state.index) {
      set_item(index);
    }
  }
  function bind() {
    if (!bind.bound) {
      bind.bound = true;
      clickables.forEach((el) =>
        el.addEventListener(opts.control_event, click)
      );
    }
  }
  function unbind() {
    if (bind.bound) {
      bind.bound = false;
      clickables.forEach((el) =>
        el.removeEventListener(opts.control_event, click)
      );
    }
  }

  // auto-sliding
  // -----------------------------------------------

  function next() {
    const i = state.index;
    const i_new =
      i === null
        ? opts.first_initially_shown
          ? 1
          : 0 // Begin at 1 if first_initially_shown, otherwise 0
        : i === items.length - 1
        ? 0
        : i + 1;

    set_item(i_new);
  }
  function start() {
    if (opts.set_initial_item && !opts.initialzied) {
      const i = state.index;
      i === null ? set_item(0) : set_item(i);
      opts.initialzied = true;
    }
    if (!state.interval) {
      state.interval = setInterval(next, opts.slide_duration);
    }
  }
  function stop() {
    clearInterval(state.interval);
    state.interval = null;
  }

  // init
  // -----------------------------------------------

  const opts = {
    auto_slide: wrapper.hasAttribute("data-cyclr--auto-slide"),
    slide_duration: parseInt(
      wrapper.getAttribute("data-cyclr--slide-duration") ||
        default_slide_duration
    ),
    first_initially_shown: wrapper.hasAttribute(
      "data-cyclr--first-initially-shown"
    ),
    stop_on_click: wrapper.hasAttribute("data-cyclr--stop-on-click"),
    items_selector: wrapper.getAttribute("data-cyclr--items-selector"),
    set_initial_item: wrapper.getAttribute("data-cyclr--set-inital-item"),
    control_event: wrapper.getAttribute("data-cyclr--control-event") ?? "click",
    initialzied: false,
  };

  const items_wrapper = opts.items_selector
    ? document.querySelector(opts.items_selector)
    : wrapper;
  if (!items_wrapper) {
    return null;
  }

  const clickables = Array.from(
    wrapper.querySelectorAll("[data-cyclr-control]")
  );
  const items = Array.from(items_wrapper.querySelectorAll("[data-cyclr-item]"));

  let state = {
    index: null,
    interval: null,
  };

  function init() {
    if (opts.auto_slide) {
      start();
    }
    if (clickables) {
      bind();
    }
    if (!clickables && !opts.auto_slide) {
      console.log(
        "cyclr.js: auto_slide must be set to true if no clickable elements are present (unless you’re going to call start() manually)"
      );
      return false;
    }
  }

  return {
    init,
    start,
    stop,
    bind,
    unbind,
    opts,
  };
}

const cyclrs = Array.from(document.querySelectorAll("[data-cyclr]")).map(cyclr);
cyclrs.forEach((cycler) => cycler.init());
